import "./special-instructions.modal.css";

import { useQueryClient } from "@tanstack/react-query";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useContext } from "react";

import {
	convertSummaryToUpdateBody,
	useAddressBook,
} from "~/features/address-book/hooks/use-address-book.ts";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import type {
	AddressBookItemSummary,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";
import { hasContactDetailData } from "~/shared/utils/address-book-detail.ts";

type SpecialInstructionsModalProps = {
	toggleModal: () => void;
};

type SpecialInstructionsModalForm = {
	specialInstructions: string;
	saveTo: string;
};

const saveToOptions = [
	{ label: "Shipper", value: "shipper" },
	{ label: "Receiver", value: "receiver" },
	{ label: "Both", value: "both" },
];

const specialInstructionsValidator = (value?: string) => {
	if (!value) {
		return "Special Instructions is required";
	}
	if (value && value.length > 500) {
		return "Special Instructions cannot exceed 500 characters";
	}
	return "";
};

const saveToValidator = (value?: string) => {
	if (!value) {
		return "Save To is required";
	}
	return "";
};

const buildUpdateBody = (
	addressBookItem: AddressBookItemSummary,
	stopType: number,
	specialInstructions: string,
): AddressBookUpdateBodyV2 => {
	const body = convertSummaryToUpdateBody(addressBookItem);

	if (body.details) {
		body.details = body.details
			.filter(
				(detail) =>
					detail.stopType === stopType || hasContactDetailData(detail),
			)
			.map((detail) => ({
				...detail,
				specialInstructions:
					detail.stopType === stopType
						? specialInstructions
						: detail.specialInstructions,
			}));
	}

	return body;
};

export const SpecialInstructionsModal = ({
	toggleModal,
}: SpecialInstructionsModalProps) => {
	const queryClient = useQueryClient();
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { updateAddressBookItem, isUpdating } = useAddressBook();

	// Automatically manage global spinner for address book updates
	useAutoSpinner(isUpdating);

	const canSaveShipper =
		!!selectedAddressBookItems.pickup?.addressBookItemId &&
		!selectedAddressBookItems.pickup?.isLegacy;
	const canSaveReceiver =
		!!selectedAddressBookItems.delivery?.addressBookItemId &&
		!selectedAddressBookItems.delivery?.isLegacy;
	const canSaveBoth = canSaveShipper && canSaveReceiver;

	const saveToOptionsWithDisabled = saveToOptions.map((option) => ({
		...option,
		disabled:
			(option.value === "shipper" && !canSaveShipper) ||
			(option.value === "receiver" && !canSaveReceiver) ||
			(option.value === "both" && !canSaveBoth),
	}));

	const onSubmit = async (values: Record<string, unknown>) => {
		const formValues = values as SpecialInstructionsModalForm;
		const { specialInstructions, saveTo } = formValues;

		const updatePromises: Promise<void>[] = [];

		// Update pickup (shipper) if selected
		if (
			(saveTo === "shipper" || saveTo === "both") &&
			selectedAddressBookItems.pickup?.addressBookItemId
		) {
			const pickupItem = selectedAddressBookItems.pickup;
			const pickupDetail = pickupItem.details.find((d) => d.stopType === 1);

			if (pickupDetail) {
				const updateBody = buildUpdateBody(pickupItem, 1, specialInstructions);
				updatePromises.push(
					updateAddressBookItem(updateBody, {
						successMessage: "Successfully saved Shipper Special Instructions",
						errorMessage: "Failure to save the Shipper Special Instructions",
					}),
				);
			}
		}

		// Update delivery (receiver) if selected
		if (
			(saveTo === "receiver" || saveTo === "both") &&
			selectedAddressBookItems.delivery?.addressBookItemId
		) {
			const deliveryItem = selectedAddressBookItems.delivery;
			const deliveryDetail = deliveryItem.details.find((d) => d.stopType === 2);

			if (deliveryDetail) {
				const updateBody = buildUpdateBody(
					deliveryItem,
					2,
					specialInstructions,
				);
				updatePromises.push(
					updateAddressBookItem(updateBody, {
						successMessage: "Successfully saved Receiver Special Instructions",
						errorMessage: "Failure to save the Receiver Special Instructions",
					}),
				);
			}
		}

		await Promise.all(updatePromises);

		await queryClient.invalidateQueries({
			queryKey: ["addressBookItems"],
		});
		await queryClient.invalidateQueries({
			queryKey: ["addressBookItemsFiltered"],
		});

		toggleModal();
	};

	return (
		<TraxModal onClose={toggleModal} title={"Save Instructions"} width={"25%"}>
			<Form
				ignoreModified={true}
				initialValues={{
					specialInstructions: quoteState.specialInstructions,
				}}
				onSubmit={(values) => {
					void onSubmit(values);
				}}
				render={() => {
					return (
						<FormElement className={"special-instructions-modal-wrapper"}>
							<ValidationInputField
								fieldType={FieldType.TEXTAREA}
								id={"specialInstructions"}
								label={"Special Instructions"}
								maxLength={500}
								name={"specialInstructions"}
								required={true}
								rows={4}
								validator={specialInstructionsValidator}
							/>
							<ValidationInputField
								data={saveToOptionsWithDisabled}
								fieldType={FieldType.RADIOGROUP}
								id={"saveTo"}
								label={"Save these instructions to either"}
								layout={"horizontal"}
								name={"saveTo"}
								required={true}
								validator={saveToValidator}
							/>
							<div className={"special-instructions-modal-actions"}>
								<Button formAction={"none"} onClick={toggleModal}>
									Cancel
								</Button>
								<Button
									disabled={isUpdating || (!canSaveShipper && !canSaveReceiver)}
									formAction={"submit"}
									variant={"primary"}
								>
									Save
								</Button>
							</div>
						</FormElement>
					);
				}}
			/>
		</TraxModal>
	);
};
